import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    ArrowLeft, CheckCircle2, XCircle, CircleDashed, SkipForward,
    CalendarDays, Save, FileText, Edit3, X, RotateCcw, Archive, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const ENROLL_BADGE = {
    active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    withdrawn: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    completed: "bg-sky-400/10 border-sky-400/30 text-sky-400",
    archived: "bg-white/[0.03] border-white/10 text-white/40",
};

const VISIT_STATUS = {
    planned: { tone: "text-white/60 border-white/20 bg-white/5", Icon: CircleDashed, label: "Planned" },
    done: { tone: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5", Icon: CheckCircle2, label: "Done" },
    missed: { tone: "text-red-400 border-red-400/30 bg-red-400/5", Icon: XCircle, label: "Missed" },
    skipped: { tone: "text-white/40 border-white/15 bg-white/[0.02]", Icon: SkipForward, label: "Skipped" },
};

function dayDelta(scheduled, today = new Date()) {
    if (!scheduled) return null;
    const s = new Date(scheduled + "T00:00:00Z");
    const t = new Date(today.toISOString().slice(0, 10) + "T00:00:00Z");
    return Math.round((s - t) / 86400000);
}

export default function EnrollmentDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rebaselineOpen, setRebaselineOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/scheduling/enrollments/${id}`);
            setDoc(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            if (e?.response?.status === 404) navigate("/scheduling");
        } finally { setLoading(false); }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const updateVisit = async (sequence, patch) => {
        setSaving(true);
        try {
            const { data } = await api.patch(
                `/admin/scheduling/enrollments/${id}/visits/${sequence}`, patch
            );
            setDoc(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const changeStatus = async (newStatus) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/scheduling/enrollments/${id}`, { status: newStatus });
            setDoc(data);
            toast.success(`Enrollment ${newStatus}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const rebaseline = async (newDate) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/scheduling/enrollments/${id}`, { baseline_date: newDate });
            setDoc(data);
            setRebaselineOpen(false);
            toast.success("Re-baselined — future planned visits recomputed");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSaving(false); }
    };

    if (loading) return <div className="px-8 py-8 text-white/40 text-sm">Loading enrollment…</div>;
    if (!doc) return <div className="px-8 py-8 text-white/40 text-sm">Enrollment not found.</div>;

    const counts = doc.visits_by_status || {};
    const total = doc.visits_total || 0;
    const done = counts.done || 0;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    return (
        <div className="px-8 py-8" data-testid="enrollment-detail-page">
            <Link to="/scheduling" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-4" data-testid="enrollment-back-link">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Back to Patient Scheduling
            </Link>

            {/* Header */}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5 mb-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-xl font-display tracking-tight font-mono">{doc.subject_ref}</h1>
                            <span className={`px-2 py-0.5 rounded border text-xs ${ENROLL_BADGE[doc.status] || ENROLL_BADGE.active}`} data-testid="enrollment-status-badge">
                                {doc.status}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-white/50">
                            <Meta label="Protocol" value={doc.protocol_number || doc.protocol_id?.slice(0, 8) + "…"} mono />
                            <Meta label="Cohort" value={doc.cohort || "—"} />
                            <Meta label="Baseline" value={doc.baseline_date} mono />
                            <Meta label="Site" value={doc.site_id} mono />
                            <Meta label="Visits" value={`${done}/${total} done (${pct}%)`} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setRebaselineOpen(true)}
                            data-testid="enrollment-rebaseline-btn"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/70 hover:text-white hover:bg-white/5 text-sm"
                        >
                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                            Re-baseline
                        </button>
                        {doc.status === "active" && (
                            <button
                                type="button"
                                onClick={() => changeStatus("completed")}
                                data-testid="enrollment-complete-btn"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 text-sm"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                Complete
                            </button>
                        )}
                        {doc.status === "active" && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (!confirm("Mark this enrollment as withdrawn?")) return;
                                    changeStatus("withdrawn");
                                }}
                                data-testid="enrollment-withdraw-btn"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-amber-400/30 text-amber-400 hover:bg-amber-400/10 text-sm"
                            >
                                <XCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
                                Withdraw
                            </button>
                        )}
                        {doc.status !== "active" && doc.status !== "archived" && (
                            <button
                                type="button"
                                onClick={() => changeStatus("active")}
                                data-testid="enrollment-reactivate-btn"
                                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/70 hover:text-white text-sm"
                            >
                                Reactivate
                            </button>
                        )}
                    </div>
                </div>
                {/* progress bar */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-emerald-400/60 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        {Object.entries(counts).map(([k, v]) => (
                            v > 0 ? (
                                <span key={k} className={`px-1.5 py-0.5 rounded border ${VISIT_STATUS[k]?.tone || ""}`}>
                                    {v} {k}
                                </span>
                            ) : null
                        ))}
                    </div>
                </div>
                {doc.notes && (
                    <p className="mt-3 text-xs text-white/60 whitespace-pre-wrap">{doc.notes}</p>
                )}
            </div>

            {/* Visit timeline */}
            <h2 className="text-sm font-display text-white/80 mb-3">
                Visit timeline <span className="text-white/40">· {total}</span>
            </h2>
            <div className="space-y-2" data-testid="enrollment-timeline">
                {(doc.visits || []).map((visit) => (
                    <VisitCard
                        key={visit.sequence}
                        visit={visit}
                        baseline={doc.baseline_date}
                        readOnly={doc.status === "archived"}
                        saving={saving}
                        onSave={(patch) => updateVisit(visit.sequence, patch)}
                    />
                ))}
            </div>

            {rebaselineOpen && (
                <RebaselineModal
                    current={doc.baseline_date}
                    onClose={() => setRebaselineOpen(false)}
                    onSubmit={rebaseline}
                />
            )}
        </div>
    );
}

function Meta({ label, value, mono }) {
    return (
        <span>
            <span className="text-white/30 mr-1">{label}</span>
            <span className={mono ? "font-mono text-white/80" : "text-white/80"}>{value}</span>
        </span>
    );
}

function VisitCard({ visit, readOnly, saving, onSave }) {
    const [editing, setEditing] = useState(false);
    const [actualDate, setActualDate] = useState(visit.actual_date || "");
    const [notes, setNotes] = useState(visit.notes || "");
    const [statusDraft, setStatusDraft] = useState(visit.status);

    useEffect(() => {
        setActualDate(visit.actual_date || "");
        setNotes(visit.notes || "");
        setStatusDraft(visit.status);
    }, [visit.status, visit.actual_date, visit.notes]);

    const meta = VISIT_STATUS[visit.status] || VISIT_STATUS.planned;
    const Icon = meta.Icon;
    const delta = dayDelta(visit.scheduled_date);
    const overdue = visit.status === "planned" && delta != null && delta < 0;

    const quickMark = async (newStatus) => {
        const patch = { status: newStatus };
        if (newStatus === "done" && !visit.actual_date) {
            patch.actual_date = new Date().toISOString().slice(0, 10);
        }
        await onSave(patch);
    };

    const saveAll = async () => {
        await onSave({
            status: statusDraft,
            actual_date: actualDate || "",
            notes,
        });
        setEditing(false);
    };

    return (
        <div
            className={`rounded-md border ${overdue ? "border-amber-400/30 bg-amber-400/[0.03]" : "border-white/10 bg-white/[0.02]"}`}
            data-testid={`visit-card-${visit.sequence}`}
        >
            <div className="flex items-center gap-3 px-4 py-3">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border ${meta.tone}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-white/40">#{visit.sequence}</span>
                        <span className="text-sm text-white truncate">{visit.name || "(unnamed)"}</span>
                        {visit.timing_label && (
                            <span className="font-mono text-[11px] text-white/40">· {visit.timing_label}</span>
                        )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-white/50">
                        {visit.scheduled_date ? (
                            <span className="font-mono">
                                <CalendarDays className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.5} />
                                {visit.scheduled_date}
                                {delta != null && (
                                    <span className={`ml-1.5 ${overdue ? "text-amber-400" : delta === 0 ? "text-emerald-400" : "text-white/40"}`}>
                                        {delta === 0 ? "today" : delta > 0 ? `in ${delta}d` : `${-delta}d ago`}
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="text-white/30">no scheduled date</span>
                        )}
                        {(visit.window_before_days > 0 || visit.window_after_days > 0) && (
                            <span className="text-white/40">
                                ±{visit.window_before_days}/{visit.window_after_days}d window
                            </span>
                        )}
                        {visit.actual_date && (
                            <span className="font-mono text-emerald-400/80">
                                actual: {visit.actual_date}
                            </span>
                        )}
                    </div>
                </div>
                <span className={`px-2 py-0.5 rounded border text-xs ${meta.tone}`} data-testid={`visit-status-${visit.sequence}`}>
                    {meta.label}
                </span>
                {!readOnly && (
                    <div className="flex items-center gap-1">
                        {visit.status !== "done" && (
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => quickMark("done")}
                                title="Mark as done (today)"
                                data-testid={`visit-mark-done-${visit.sequence}`}
                                className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-emerald-400"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                            </button>
                        )}
                        {visit.status !== "missed" && visit.status !== "planned" && (
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => quickMark("planned")}
                                title="Reset to planned"
                                data-testid={`visit-reset-${visit.sequence}`}
                                className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white"
                            >
                                <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={saving}
                            onClick={() => setEditing(!editing)}
                            title="Edit"
                            data-testid={`visit-edit-${visit.sequence}`}
                            className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white"
                        >
                            {editing ? <X className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        </button>
                    </div>
                )}
            </div>

            {editing && !readOnly && (
                <div className="border-t border-white/10 px-4 py-3 space-y-3 text-xs">
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-white/40 mb-1">Status</div>
                            <select
                                value={statusDraft}
                                onChange={(e) => setStatusDraft(e.target.value)}
                                data-testid={`visit-status-select-${visit.sequence}`}
                                className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                            >
                                {Object.keys(VISIT_STATUS).map((s) => (
                                    <option key={s} value={s}>{VISIT_STATUS[s].label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <div className="text-white/40 mb-1">Actual date (when it happened)</div>
                            <input
                                type="date"
                                value={actualDate || ""}
                                onChange={(e) => setActualDate(e.target.value)}
                                data-testid={`visit-actual-date-${visit.sequence}`}
                                className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5 font-mono"
                            />
                        </label>
                    </div>
                    <label className="block">
                        <div className="text-white/40 mb-1">Notes</div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            data-testid={`visit-notes-${visit.sequence}`}
                            className="w-full bg-[#050505] border border-white/15 rounded px-2 py-1.5"
                        />
                    </label>
                    <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={() => setEditing(false)} className="px-3 py-1.5 rounded text-white/60 hover:text-white">
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={saveAll}
                            data-testid={`visit-save-${visit.sequence}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300"
                        >
                            <Save className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Save visit
                        </button>
                    </div>
                </div>
            )}

            {/* Required forms — chips that link to the form editor */}
            {(visit.forms || []).length > 0 && (
                <div className="border-t border-white/10 px-4 py-3 flex flex-wrap items-center gap-2" data-testid={`visit-forms-${visit.sequence}`}>
                    <span className="label-xs text-white/40 inline-flex items-center gap-1">
                        <FileText className="w-3 h-3" strokeWidth={1.75} />
                        Forms required at this visit
                    </span>
                    {visit.forms.map((f) => (
                        <Link
                            key={f.id}
                            to={`/forms/${f.id}`}
                            data-testid={`visit-form-chip-${visit.sequence}-${f.id}`}
                            className={`px-2 py-0.5 rounded border text-[11px] hover:bg-white/5 transition-colors ${
                                f.status === "published"
                                    ? "border-emerald-400/30 text-emerald-300"
                                    : f.status === "archived"
                                    ? "border-white/15 text-white/40"
                                    : "border-white/20 text-white/70"
                            }`}
                            title={`${f.form_name} · ${f.field_count} fields · ${f.status}`}
                        >
                            {f.form_name}
                            {f.cdisc_domain && <span className="ml-1 text-white/40">·{f.cdisc_domain}</span>}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function RebaselineModal({ current, onClose, onSubmit }) {
    const [newDate, setNewDate] = useState(current);
    return (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rebaseline-modal">
            <div className="w-full max-w-md rounded-lg border border-white/15 bg-[#0a0a0a]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <h2 className="text-sm font-medium">Re-baseline this enrollment</h2>
                    <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="rebaseline-close-btn">
                        <X className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="p-5 space-y-4 text-xs">
                    <div className="flex items-start gap-2 px-3 py-2 rounded border border-amber-400/20 bg-amber-400/5 text-amber-300/90">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.75} />
                        <span>
                            Re-baselining only recomputes <strong>scheduled dates of visits still in planned status</strong>.
                            Visits already marked done / missed / skipped are preserved.
                        </span>
                    </div>
                    <label className="block">
                        <div className="text-white/40 mb-1">New baseline date</div>
                        <input
                            type="date"
                            value={newDate}
                            onChange={(e) => setNewDate(e.target.value)}
                            data-testid="rebaseline-date-input"
                            className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 font-mono"
                            autoFocus
                        />
                    </label>
                    <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-white/60 hover:text-white">
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={!newDate}
                            onClick={() => onSubmit(newDate)}
                            data-testid="rebaseline-submit-btn"
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50"
                        >
                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Re-baseline
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
